import React from "react";
import PropTypes from "prop-types";
import ContentBlockAccordion from "./ContentBlockAccordion";
import {
  StyledContentSection,
  ContentBlockItemWrapper,
  Container,
  HiddenContainer,
  StyledContentBlock,
  ListStyledContentBlock,
  SubSectionsWrapper,
  PrintSubSectionsWrapper,
} from "./_.styled";
import { Title } from "../Title/_.styled";
import { PUBTYPE } from "../../constants/pubType";

class ContentBlockItem extends React.PureComponent {
  render() {
    const {
      content,
      isValidSplitContent,
      idToSectionMap,
      activeSectionIndex,
      selectedReferences,
      preProcessors,
      referenceSectionId,
      careInterventionsSectionId,
      useNumberedRef = true,
      slugPubType,
    } = this.props;

    const isLeadershipTopic = slugPubType === PUBTYPE.LEADERSHIP;
    // If any subsection is shown, then show the parent
    const hasPrintSelectedSubSection = (subSections) => {
      let found = false;
      if (subSections) {
        subSections.forEach((subsection) => {
          if (selectedReferences.includes(subsection.id)) {
            found = true;
          }
        });
      }
      return found;
    };

    const renderSubsections = (options) => {
      const {
        subSections,
        isReferenceSection,
        isCareInterventionsSection,
        childSelectedReferences,
        parentSelectedForPrint,
        depth = 0,
      } = options;

      let typeElement = "h6";
      switch (depth) {
        case 0:
          typeElement = "h3";
          break;
        case 1:
          typeElement = "h4";
          break;
        case 2:
          typeElement = "h5";
          break;
        default:
      }
      return (
        <>
          {subSections && (
            <SubSectionsWrapper
              isNumberedList={useNumberedRef && isReferenceSection}
              parentSelectedForPrint={parentSelectedForPrint}
            >
              {subSections.map((subContentItem) => {
                const isAccordion =
                  subContentItem.contentEnhancements &&
                  subContentItem.contentEnhancements.accordion;
                const testId = isCareInterventionsSection
                  ? "care-intervention-accordion"
                  : "content-enhancements-accordion";
                return (
                  <>
                    {isCareInterventionsSection || isAccordion ? (
                      <PrintSubSectionsWrapper
                        key={subContentItem.id}
                        printHide={
                          !childSelectedReferences.includes(subContentItem.id)
                        }
                      >
                        <ContentBlockAccordion
                          testId={testId}
                          subContentItem={subContentItem}
                          childSelectedReferences={childSelectedReferences}
                          isAccordion={isAccordion}
                          preProcessors={preProcessors}
                          isPrintSelected={childSelectedReferences.includes(
                            subContentItem.id
                          )}
                        ></ContentBlockAccordion>
                      </PrintSubSectionsWrapper>
                    ) : (
                      <>
                        <ContentBlockItemWrapper
                          id={subContentItem.id}
                          key={subContentItem.id}
                          hasIncreasedHeadingSpacing={isLeadershipTopic}
                          printHide={
                            !childSelectedReferences.includes(subContentItem.id)
                          }
                        >
                          <Title
                            className={typeElement}
                            element={typeElement}
                            fontFamily="secondary"
                          >
                            {subContentItem.title}
                          </Title>

                          <ListStyledContentBlock
                            children={subContentItem.data}
                            preProcessors={preProcessors}
                          />
                        </ContentBlockItemWrapper>
                        {subContentItem?.subSections &&
                          renderSubsections({
                            subSections: subContentItem.subSections,
                            isReferenceSection,
                            isCareInterventionsSection,
                            childSelectedReferences,
                            depth: depth + 1,
                          })}
                      </>
                    )}
                  </>
                );
              })}
            </SubSectionsWrapper>
          )}
        </>
      );
    };

    return (
      <StyledContentSection data-testid="content-section-wrapper">
        {content &&
          content.map((contentItem) => {
            if (!contentItem) {
              return <></>;
            }
            const isReferenceSection = referenceSectionId === contentItem.id;
            const isCareInterventionsSection =
              careInterventionsSectionId === contentItem.id;
            const { subSections } = contentItem;

            // If isValidSplitContent only render current section and
            // sections selected for print
            return !isValidSplitContent ||
              idToSectionMap[contentItem.id]?.index === activeSectionIndex ? (
              <Container
                key={contentItem.id}
                $isEmphasized={
                  contentItem.contentEnhancements?.emphasized?.isEnabled
                }
              >
                <ContentBlockItemWrapper
                  printHide={
                    !selectedReferences.includes(contentItem.id) &&
                    !hasPrintSelectedSubSection(contentItem.subSections)
                  }
                  hasIncreasedHeadingSpacing={isLeadershipTopic}
                  id={contentItem.id}
                >
                  <Title
                    element="h2"
                    fontFamily="secondary"
                    color="headingprimary"
                  >
                    {contentItem.title}
                  </Title>
                  {contentItem.data && (
                    <StyledContentBlock
                      children={contentItem.data}
                      preProcessors={preProcessors}
                    />
                  )}
                </ContentBlockItemWrapper>
                {renderSubsections({
                  subSections,
                  isReferenceSection,
                  isCareInterventionsSection,
                  childSelectedReferences: selectedReferences,
                  parentSelectedForPrint: selectedReferences.includes(
                    contentItem.id
                  ),
                })}
              </Container>
            ) : (
              // Only create the hidden section for print
              // If section or any subsection are selected for print
              (selectedReferences.includes(contentItem.id) ||
                hasPrintSelectedSubSection(contentItem.subSections)) && (
                <HiddenContainer key={contentItem.id}>
                  <ContentBlockItemWrapper
                    id={contentItem.id}
                    hasIncreasedHeadingSpacing={isLeadershipTopic}
                  >
                    <Title
                      element="h2"
                      color="secondary"
                      fontFamily="secondary"
                    >
                      {contentItem.title}
                    </Title>

                    {contentItem.data && (
                      <StyledContentBlock
                        children={contentItem.data}
                        preProcessors={preProcessors}
                      />
                    )}
                  </ContentBlockItemWrapper>
                  {renderSubsections({
                    subSections,
                    isReferenceSection,
                    isCareInterventionsSection,
                    childSelectedReferences: selectedReferences,
                    parentSelectedForPrint: selectedReferences.includes(
                      contentItem.id
                    ),
                  })}
                </HiddenContainer>
              )
            );
          })}
      </StyledContentSection>
    );
  }
}

ContentBlockItem.propTypes = {
  content: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      id: PropTypes.string,
      data: PropTypes.string,
      subSections: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string,
          id: PropTypes.string,
          data: PropTypes.string,
        })
      ),
    })
  ).isRequired,
  selectedReferences: PropTypes.array,
  preProcessors: PropTypes.array,
};

ContentBlockItem.defaultProps = {
  selectedReferences: [],
};

ContentBlockItem.displayName = "ContentBlockItem";

export default ContentBlockItem;
